import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api } from './lib/api';
import { getSupabase } from './lib/supabase';
import { C, HEADER_HEIGHT, ink, PAGE_BG } from './lib/theme';
import { useFilters } from './lib/useFilters';
import type { LostEvent, Me, Reason, Tab } from './lib/types';
import { FilterBar } from './components/FilterBar';
import { AppSidebar } from './components/AppSidebar';
import { ClientDrawer } from './components/ClientDrawer';
import { ContactModal } from './components/ContactModal';
import { EvidenceModal } from './components/EvidenceModal';
import type { EventCorrectionBody } from './components/EventCard';
import type { Origin } from './components/ScaleModal';
import { FilaPage } from './pages/FilaPage';
import { ConfirmadosPage } from './pages/ConfirmadosPage';
import { VisaoGeralPage } from './pages/VisaoGeralPage';
import { ClientesPage } from './pages/ClientesPage';
import { ReasonsAdmin } from './pages/ReasonsAdmin';
import { UsersAdmin } from './pages/UsersAdmin';
import { ProfileMenu } from './components/ProfileMenu';
import { AuditExportButton } from './components/AuditExportButton';
import { RecoveredButton } from './components/RecoveredButton';
import { SettingsModal } from './components/SettingsModal';

/**
 * "Confirmados" saiu daqui de propósito: virou um painel aberto de dentro da
 * Fila (`ConfirmadosPanel`), porque conferir o que já foi validado é consulta
 * pontual dentro da auditoria — trocar de aba custava a posição na fila. A
 * rota `/confirmados` continua existindo pra não quebrar link antigo.
 */
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'fila', label: 'Fila' },
  { id: 'visao-geral', label: 'Visão Geral' },
];

const SIDEBAR_STORAGE_KEY = 'revops-sidebar-collapsed';
function readStoredSidebarCollapsed(): boolean {
  try { return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1'; } catch { return false; }
}

export function App({ me, onSignOut, onMeUpdated }: { me: Me; onSignOut: () => void; onMeUpdated: (patch: Partial<Me>) => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { filters, setFilter } = useFilters();
  const channel = filters.channel ?? 'direto';

  const [reasons, setReasons] = useState<Reason[]>([]);
  const [facets, setFacets] = useState<{ categories: string[]; sources: string[] }>({ categories: [], sources: [] });
  const [partners, setPartners] = useState<string[]>([]);

  const [clientId, setClientId] = useState<string | null>(null);
  const [contact, setContact] = useState<{ masterId: string; origin: Origin } | null>(null);
  const [evidence, setEvidence] = useState<{
    event: LostEvent; origin: Origin;
    onSetCorrection: (ev: LostEvent, body: EventCorrectionBody) => void;
    onClearCorrection: (ev: LostEvent) => void;
  } | null>(null);
  const [settingsOrigin, setSettingsOrigin] = useState<Origin | null>(null);
  const [filaCount, setFilaCount] = useState<number | null>(null);
  // Estado da sidebar de módulos vive aqui (não dentro do componente) porque o
  // grid da página precisa reagir a ele — colapsar tem que devolver a coluna
  // liberada pro conteúdo, não só encolher os ícones deixando um vão vazio.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readStoredSidebarCollapsed);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_STORAGE_KEY, sidebarCollapsed ? '1' : '0'); } catch { /* modo privado — sem persistência, sem problema */ }
  }, [sidebarCollapsed]);

  const tab: Tab = location.pathname.startsWith('/confirmados') ? 'confirmados'
    : location.pathname.startsWith('/visao-geral') ? 'visao-geral' : 'fila';

  const isAdmin = location.pathname.startsWith('/admin');
  const isClientes = location.pathname.startsWith('/clientes');

  // Motivos são usados pelo classificador de TODO card e pelo filtro — carrega
  // uma vez e compartilha, em vez de uma request por dropdown aberto.
  useEffect(() => {
    api.listReasons().then(setReasons).catch(() => setReasons([]));
  }, []);

  // Opções de Categoria/Fonte saem do heatmap e de uma amostra da lista: o
  // backend não expõe endpoint de facetas, e derivar do dado já carregado evita
  // inventar uma rota nova só pra popular dois filtros.
  // Opções dos filtros saem do canal ativo — Parceiros não oferece categoria/fonte que só existe no Direto.
  useEffect(() => {
    let alive = true;
    Promise.all([api.heatmap({ channel }), api.listEvents({ channel }, 'todos', undefined, 200)])
      .then(([heat, events]) => {
        if (!alive) return;
        setFacets({
          categories: [...new Set(heat.map((h) => h.product_category).filter((c): c is string => !!c))].sort(),
          sources: [...new Set(events.items.map((e) => e.source_system).filter((s): s is string => !!s))].sort(),
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [channel]);

  useEffect(() => {
    let alive = true;
    api.listEvents({ channel: 'parceiro' }, 'todos', undefined, 200)
      .then((r) => {
        if (!alive) return;
        setPartners([...new Set(r.items.map((e) => e.partner_name).filter((p): p is string => !!p))].sort());
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const setChannel = (c: 'direto' | 'parceiro') => {
    if (c === channel) return;
    setFilter('partner_name', undefined);
    setFilter('channel', c === 'direto' ? undefined : c);
  };

  // Badge da aba Fila: contagem de pendentes no filtro atual.
  useEffect(() => {
    let alive = true;
    api.filaKpis(filters)
      .then((k) => { if (alive) setFilaCount(Number(k.total_eventos ?? 0)); })
      .catch(() => { if (alive) setFilaCount(null); });
    return () => { alive = false; };
  }, [JSON.stringify(filters), location.pathname]);

  const cardHandlers = useMemo(() => ({
    onOpenClient: (masterId: string) => setClientId(masterId),
    onOpenContacts: (ev: LostEvent, origin: Origin) => setContact({ masterId: ev.master_id, origin }),
    onOpenEvidence: (
      ev: LostEvent, origin: Origin,
      onSetCorrection: (ev: LostEvent, body: EventCorrectionBody) => void,
      onClearCorrection: (ev: LostEvent) => void,
    ) => setEvidence({ event: ev, origin, onSetCorrection, onClearCorrection }),
  }), []);

  const goTab = (t: Tab) => navigate({ pathname: `/${t}`, search: location.search });

  return (
    <div style={{
      position: 'relative', minHeight: '100vh', background: PAGE_BG, color: C.fg, overflowX: 'hidden',
    }}>
      {/* Orbs à deriva + grão: a "profundidade atmosférica" do PRD §10, herdada
          da capa da apresentação de marca. pointer-events:none pra nunca roubar clique. */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <div style={{
          position: 'absolute', width: 620, height: 620, borderRadius: '50%', filter: 'blur(100px)',
          opacity: 0.3, top: -220, left: -140,
          background: `radial-gradient(circle at 40% 40%, ${C.accentStrong}, transparent 68%)`,
          animation: 'koDrift1 18s ease-in-out infinite alternate',
        }} />
        <div style={{
          position: 'absolute', width: 460, height: 460, borderRadius: '50%', filter: 'blur(100px)',
          opacity: 0.16, bottom: -200, right: -120,
          background: `radial-gradient(circle at 60% 40%, ${C.accentDeep}, transparent 66%)`,
          animation: 'koDrift2 22s ease-in-out infinite alternate',
        }} />
      </div>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.045,
        mixBlendMode: 'overlay',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      <header style={{
        position: 'sticky', top: 0, zIndex: 60, display: 'flex', alignItems: 'center', gap: 26,
        padding: '0 30px', height: HEADER_HEIGHT, background: 'var(--header-bg)',
        backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
        borderBottom: `1px solid ${ink(0.09)}`,
      }}>
        {/* Marca com mais presença — pedido explícito de aumentar o header:
            logo maior, divisor mais alto, wordmark com mais peso/contraste. */}
        <Link to={{ pathname: '/fila', search: location.search }} style={{
          display: 'flex', alignItems: 'center', gap: 16, flex: 'none', textDecoration: 'none',
        }}>
          <img src="/assets/kory-logo.png" alt="Kory" style={{
            height: 32, width: 'auto', display: 'block', filter: 'drop-shadow(0 2px 12px rgba(255,107,71,0.4))',
          }} />
          <div style={{ width: 1, height: 30, background: ink(0.2) }} />
          <span style={{
            fontSize: 13.5, fontWeight: 780, letterSpacing: '0.15em', textTransform: 'uppercase', color: ink(0.72),
          }}>RevOps Hub</span>
        </Link>

        {!isAdmin && !isClientes && (
          <div role="radiogroup" aria-label="Canal" style={{
            display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto',
            border: `1px solid ${ink(0.09)}`, borderRadius: 12, padding: 3,
          }}>
            {([['direto', 'Direto'], ['parceiro', 'Parceiros']] as const).map(([id, label]) => {
              const on = channel === id;
              return (
                <button key={id} type="button" role="radio" aria-checked={on} onClick={() => setChannel(id)} style={{
                  border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12.5,
                  fontWeight: on ? 750 : 600, padding: '6px 14px', borderRadius: 9,
                  background: on ? ink(0.12) : 'transparent',
                  color: on ? C.fg : ink(0.5),
                  whiteSpace: 'nowrap',
                }}>{label}</button>
              );
            })}
          </div>
        )}

        {!isAdmin && !isClientes && (
          <nav style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: ink(0.05), border: `1px solid ${ink(0.09)}`, borderRadius: 14, padding: 4,
          }}>
            {TABS.map((t) => {
              const on = tab === t.id;
              return (
                <button key={t.id} type="button" onClick={() => goTab(t.id)} style={{
                  border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 13.5,
                  fontWeight: on ? 750 : 600, letterSpacing: '-0.005em', padding: '8px 18px', borderRadius: 11,
                  background: on ? `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})` : 'transparent',
                  color: on ? '#fff' : ink(0.55),
                  boxShadow: on ? '0 6px 18px rgba(232,85,48,0.3)' : 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
                }}>
                  {t.label}
                  {t.id === 'fila' && filaCount !== null && filaCount > 0 && (
                    <span style={{
                      fontSize: 11.5, fontWeight: 750, borderRadius: 99, padding: '1px 7px',
                      background: on ? 'rgba(255,255,255,0.22)' : ink(0.1),
                    }}>{filaCount}</span>
                  )}
                </button>
              );
            })}
          </nav>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flex: 'none', marginLeft: (isAdmin || isClientes) ? 'auto' : 0 }}>
          {me.role === 'admin' && <AuditExportButton period={{ from: filters.from, to: filters.to }} />}
          <RecoveredButton period={{ from: filters.from, to: filters.to, channel }} />
          <Link to="/admin/motivos" style={{
            fontSize: 12, fontWeight: 600, color: isAdmin ? C.accent : ink(0.42),
            letterSpacing: '0.01em', textDecoration: 'none',
          }}>Motivos</Link>
          <ProfileMenu me={me} onSignOut={onSignOut} onOpenSettings={setSettingsOrigin} />
        </div>
      </header>

      {!isAdmin && !isClientes && (
        <FilterBar tab={tab} reasons={reasons} categories={facets.categories} sources={facets.sources} partners={partners} />
      )}

      {isAdmin ? (
        <div style={{ position: 'relative', zIndex: 2 }}>
          <Routes>
            <Route
              path="/admin/motivos"
              element={me.role === 'admin' ? <ReasonsAdmin /> : <Navigate to="/fila" replace />}
            />
            <Route
              path="/admin/usuarios"
              element={me.role === 'admin' ? <UsersAdmin me={me} /> : <Navigate to="/fila" replace />}
            />
            <Route path="/admin/*" element={<Navigate to="/admin/motivos" replace />} />
          </Routes>
        </div>
      ) : (
        <div style={{
          position: 'relative', zIndex: 2, display: 'grid',
          gridTemplateColumns: sidebarCollapsed ? '82px minmax(0,1fr)' : '184px minmax(0,1fr)',
          transition: 'grid-template-columns 0.18s ease',
          gap: 26, padding: '30px 30px 90px 14px', maxWidth: 1600, margin: '0 auto',
        }}>
          <AppSidebar
            collapsed={sidebarCollapsed} onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
          />
          <main style={{ minWidth: 0 }}>
            <Routes>
              <Route path="/" element={<Navigate to="/fila" replace />} />
              <Route path="/fila" element={
                <FilaPage filters={filters} reasons={reasons} handlers={cardHandlers} />
              } />
              <Route path="/confirmados" element={
                <ConfirmadosPage filters={filters} reasons={reasons} handlers={cardHandlers} />
              } />
              <Route path="/visao-geral" element={
                <VisaoGeralPage filters={filters} onOpenClient={setClientId} />
              } />
              <Route path="/clientes" element={
                <ClientesPage onOpenClient={setClientId} />
              } />
              <Route path="*" element={<Navigate to="/fila" replace />} />
            </Routes>
          </main>
        </div>
      )}

      <ContactModal
        masterId={contact?.masterId ?? null} origin={contact?.origin ?? null}
        onClose={() => setContact(null)} onOpenClient={setClientId}
      />
      <EvidenceModal
        event={evidence?.event ?? null} origin={evidence?.origin ?? null}
        onClose={() => setEvidence(null)}
        onSetCorrection={evidence?.onSetCorrection} onClearCorrection={evidence?.onClearCorrection}
      />
      <ClientDrawer masterId={clientId} onClose={() => setClientId(null)} />
      <SettingsModal me={me} origin={settingsOrigin} onClose={() => setSettingsOrigin(null)} onMeUpdated={onMeUpdated} />
    </div>
  );
}

/** Wrapper que decide entre login e app — mantém o App puro de lógica de sessão. */
export function signOut() {
  return getSupabase().auth.signOut();
}
